import { useMemo } from 'react'
import { C, getInterval, getBuilding } from './utils'

function StatCard({label,value,sub,color='#0f2237',icon}) {
  return <div style={{background:'#fff',borderRadius:12,padding:'16px',boxShadow:'0 1px 4px rgba(0,0,0,.07)',borderTop:`3px solid ${color}`}}>
    <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>{icon} {label}</div>
    <div style={{fontSize:28,fontWeight:800,color,lineHeight:1,marginBottom:4}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:C.muted}}>{sub}</div>}
  </div>
}

function BarChart({data, color=C.steel, labelKey='label', valueKey='value', title}) {
  const max = Math.max(...data.map(d=>d[valueKey]),1)
  return <div>
    {title&&<div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:12}}>{title}</div>}
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      {data.map((d,i)=><div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
        <div style={{width:90,fontSize:11,color:C.muted,textAlign:'right',flexShrink:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d[labelKey]}</div>
        <div style={{flex:1,background:'#f4f6f9',borderRadius:20,height:22,overflow:'hidden'}}>
          <div style={{width:`${(d[valueKey]/max)*100}%`,background:color,height:'100%',borderRadius:20,minWidth:d[valueKey]>0?4:0,display:'flex',alignItems:'center',paddingLeft:8,transition:'width .3s'}}>
            {d[valueKey]>0&&<span style={{fontSize:10,fontWeight:700,color:'#fff',whiteSpace:'nowrap'}}>{d[valueKey]}</span>}
          </div>
        </div>
      </div>)}
    </div>
  </div>
}

const _today = new Date(); _today.setHours(0,0,0,0)
const _thisMonth = _today.getMonth()
const _thisYear = _today.getFullYear()

export default function ReportsView({units, changelog, inventory, users, season}) {
  const today = _today
  const thisMonth = _thisMonth
  const thisYear = _thisYear

  // Changes this month
  const changesThisMonth = useMemo(()=>changelog.filter(l=>{
    const d=new Date(l.change_date)
    return d.getMonth()===thisMonth&&d.getFullYear()===thisYear
  }),[changelog,thisMonth,thisYear])

  // Changes last 6 months
  const last6Months = useMemo(()=>{
    const months=[]
    for(let i=5;i>=0;i--){
      const d=new Date(thisYear,thisMonth-i,1)
      const m=d.getMonth(),y=d.getFullYear()
      const count=changelog.filter(l=>{const ld=new Date(l.change_date);return ld.getMonth()===m&&ld.getFullYear()===y}).length
      months.push({label:d.toLocaleDateString('en-US',{month:'short'}),value:count})
    }
    return months
  },[changelog,thisMonth,thisYear])

  // Per technician this month
  const perTech = useMemo(()=>{
    const counts={}
    changesThisMonth.forEach(l=>{counts[l.worker_name]=(counts[l.worker_name]||0)+1})
    return Object.entries(counts).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value)
  },[changesThisMonth])

  // Per building this month
  const perBuilding = useMemo(()=>{
    const counts={}
    changesThisMonth.forEach(l=>{const b=getBuilding(l.area);counts[b]=(counts[b]||0)+1})
    return Object.entries(counts).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value)
  },[changesThisMonth])

  // Overdue units
  const overdueUnits = useMemo(()=>units.filter(u=>{
    const iv=getInterval(u,season)
    if(!iv||!u.last_changed) return false
    const due=new Date(u.last_changed); due.setDate(due.getDate()+iv)
    return due<today
  }),[units,season,today])

  // Smart interval suggestions — units always found dirty
  const intervalSuggestions = useMemo(()=>{
    const suggestions=[]
    units.forEach(u=>{
      const history=changelog.filter(l=>l.unit_id===u.id)
      if(history.length<3) return // need at least 3 data points
      const dirty=history.filter(l=>['Dirty','Very Dirty','Damaged'].includes(l.condition))
      const dirtyRate=dirty.length/history.length
      if(dirtyRate>=0.6){
        const iv=getInterval(u,season)
        if(iv&&iv>14){
          suggestions.push({
            unit:u,
            dirtyRate:Math.round(dirtyRate*100),
            currentInterval:iv,
            suggestedInterval:Math.round(iv*0.7),
            history:history.length
          })
        }
      }
    })
    return suggestions
  },[units,changelog,season])

  // Predictive stock depletion
  const stockPrediction = useMemo(()=>{
    // Calculate monthly usage per filter size from last 3 months
    const threeMonthsAgo=new Date(thisYear,thisMonth-3,1)
    const recentChanges=changelog.filter(l=>new Date(l.change_date)>=threeMonthsAgo)
    const usage={}
    recentChanges.forEach(l=>{
      if(l.size&&l.size!=='Washable') usage[l.size]=(usage[l.size]||0)+l.qty_used
    })
    return inventory.filter(i=>i.size!=='Washable'&&usage[i.size]>0).map(i=>{
      const monthlyUsage=usage[i.size]/3
      const monthsLeft=monthlyUsage>0?i.stock/monthlyUsage:null
      return {...i,monthlyUsage:Math.round(monthlyUsage*10)/10,monthsLeft:monthsLeft?Math.round(monthsLeft*10)/10:null}
    }).sort((a,b)=>(a.monthsLeft??99)-(b.monthsLeft??99))
  },[inventory,changelog,thisMonth,thisYear])

  // Inventory audit — last 20 changes tracked via change_log deductions
  const inventoryUsage = useMemo(()=>{
    const usage={}
    changelog.forEach(l=>{
      if(l.size&&l.size!=='Washable'){
        if(!usage[l.size]) usage[l.size]={size:l.size,total:0,count:0}
        usage[l.size].total+=l.qty_used||0
        usage[l.size].count++
      }
    })
    return Object.values(usage).sort((a,b)=>b.total-a.total)
  },[changelog])

  const Card=({children,title,style={}})=><div style={{background:'#fff',borderRadius:12,boxShadow:'0 1px 4px rgba(0,0,0,.07)',overflow:'hidden',marginBottom:16,...style}}>
    {title&&<div style={{padding:'12px 18px',borderBottom:`1px solid ${C.border}`,fontWeight:700,fontSize:14,color:C.text}}>{title}</div>}
    <div style={{padding:'16px 18px'}}>{children}</div>
  </div>

  return <div>
    <div style={{marginBottom:16}}>
      <h1 style={{fontSize:22,fontWeight:800,color:C.text,letterSpacing:'-.02em'}}>Reports</h1>
      <p style={{color:C.muted,fontSize:11,marginTop:2}}>Analytics and insights for {today.toLocaleDateString('en-US',{month:'long',year:'numeric'})}</p>
    </div>

    {/* Summary stats */}
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:16}}>
      <StatCard label="Changes This Month" value={changesThisMonth.length} icon="✓" color={C.steel}/>
      <StatCard label="Overdue Units" value={overdueUnits.length} icon="🔴" color="#c0392b"/>
      <StatCard label="Total Changes" value={changelog.length} icon="📋" color={C.navy}/>
      <StatCard label="Active Units" value={units.length} icon="⚙" color="#1e8449"/>
      <StatCard label="Low Stock" value={inventory.filter(i=>i.stock<i.min_stock).length} icon="📦" color="#d35400"/>
    </div>

    {/* Changes over 6 months */}
    <Card title="📊 Filter Changes — Last 6 Months">
      <BarChart data={last6Months} color={C.steel}/>
    </Card>

    {/* Per technician */}
    {perTech.length>0&&<Card title="👤 Changes by Technician This Month">
      <BarChart data={perTech} color="#1e6b44"/>
    </Card>}

    {/* Per building */}
    {perBuilding.length>0&&<Card title="🏢 Changes by Building This Month">
      <BarChart data={perBuilding} color={C.navy}/>
    </Card>}

    {/* Smart interval suggestions */}
    {intervalSuggestions.length>0&&<Card title="💡 Smart Interval Suggestions">
      <p style={{fontSize:12,color:C.muted,marginBottom:14}}>These units are consistently found dirty. Consider shortening their change interval.</p>
      {intervalSuggestions.map(({unit,dirtyRate,currentInterval,suggestedInterval,history})=><div key={unit.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:`1px solid ${C.border}`}}>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:C.text,fontSize:13}}>{unit.location}</div>
          <div style={{fontSize:11,color:C.muted}}>{unit.area} · {history} logged changes · {dirtyRate}% found dirty</div>
        </div>
        <div style={{textAlign:'right',flexShrink:0}}>
          <div style={{fontSize:11,color:C.muted,textDecoration:'line-through'}}>{currentInterval} days</div>
          <div style={{fontSize:14,fontWeight:800,color:'#d35400'}}>→ {suggestedInterval} days</div>
        </div>
      </div>)}
    </Card>}

    {/* Predictive stock depletion */}
    {stockPrediction.length>0&&<Card title="📦 Predicted Stock Depletion">
      <p style={{fontSize:12,color:C.muted,marginBottom:14}}>Based on usage over the last 3 months. For information only.</p>
      <table style={{width:'100%',borderCollapse:'collapse'}}>
        <thead><tr>
          {['Filter Size','In Stock','Monthly Use','Months Left','Status'].map(h=><th key={h} style={{padding:'8px 10px',fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',borderBottom:`1px solid ${C.border}`,textAlign:'left'}}>{h}</th>)}
        </tr></thead>
        <tbody>{stockPrediction.map(i=>{
          const urgent=i.monthsLeft!==null&&i.monthsLeft<2
          const warn=i.monthsLeft!==null&&i.monthsLeft<4
          const color=urgent?'#c0392b':warn?'#d35400':'#1e8449'
          const bg=urgent?'#fdecea':warn?'#fef0e6':'#eafaf1'
          return <tr key={i.size} style={{borderBottom:`1px solid ${C.border}`}}>
            <td style={{padding:'9px 10px',fontFamily:'monospace',fontWeight:700,color:C.navy,fontSize:12}}>{i.size}</td>
            <td style={{padding:'9px 10px',fontSize:12,textAlign:'center',fontWeight:700,color:i.stock<i.min_stock?'#c0392b':C.text}}>{i.stock}</td>
            <td style={{padding:'9px 10px',fontSize:12,textAlign:'center',color:C.muted}}>{i.monthlyUsage}/mo</td>
            <td style={{padding:'9px 10px',fontSize:12,textAlign:'center',fontWeight:700,color}}>{i.monthsLeft!==null?`${i.monthsLeft} mo`:'—'}</td>
            <td style={{padding:'9px 10px'}}><span style={{background:bg,color,padding:'3px 8px',borderRadius:20,fontSize:10,fontWeight:700}}>{urgent?'Order Soon':warn?'Watch':'OK'}</span></td>
          </tr>
        })}</tbody>
      </table>
    </Card>}

    {/* Inventory usage */}
    {inventoryUsage.length>0&&<Card title="📋 Inventory Usage — All Time">
      <BarChart data={inventoryUsage.slice(0,10)} labelKey="size" valueKey="total" color={C.steel}/>
    </Card>}

    {changelog.length===0&&<div style={{background:'#eef5fb',borderRadius:12,padding:32,textAlign:'center',color:C.muted}}>
      No change log data yet. Reports will populate as filter changes are logged.
    </div>}
  </div>
}
